<template>
  <div class="container mt-5 px-2">
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link :to="vendorId ? ('/vendor-purchase-orders/' + vendorId) : '/purchase-orders'">Purchase Orders</router-link></li>
          <li class="is-active"><a>{{ poId }}</a></li>
        </ul>
      </nav>
      <div class="mt-3">
        <div v-if="waiting || !po">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>
        <div v-else>
          <span class="is-pulled-right">
            <a v-if="(isVendor || isVendorAdmin) && po && !po.confirmedBy" class="button is-danger mr-2" :class="{'is-loading': confirming}" @click="confirmPO">Confirm PO</a>
            <a v-if="po && po.statusId != 'Closed'" class="button mr-2" :class="{'is-loading': syncing}" @click="syncFromNS">Sync from NS</a>
          </span>
          <h1 class="title is-5 mt-6">Primary info</h1>
          <div class="columns is-multiline mt-3">

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">PO#</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.tranId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">NS Id</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.nsId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Approval</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.approvalStatusName" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Status</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.statusId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Subsidiary</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.subsidiaryName" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Vendor</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.vendorName" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Location</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.locationName" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Memo</label>
                <div class="control">
                  <textarea class="textarea" placeholder="Textarea" readonly>{{ po.memo }}</textarea>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Confirmed by</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.confirmedBy" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Transaction Date</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.tranDate" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Created Date</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.createdDate" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">

              <div class="field mt-3">
                <label class="label">Last modified date</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="po.lastModifiedDate" readonly>
                </div>
              </div>
            </div>
            
          </div>

          <hr/>

          <div class="mt-6 table-container">
            <h1 class="title is-5 mt-6">
              <span>PO Items</span>&nbsp;
              <button class="button">
                <span class="icon">
                  <i class="fas fa-download" @click="exportItems"></i>
                </span>
              </button>
            </h1>
            <table v-if="po.items" class="table is-full-width is-hoverable my-table">
              <thead>
                <th>Line</th>
                <th>NS id</th>
                <th>Item</th>
                <th>Description</th>
                <th class="num-cell">
                  <span>On hand</span><br/>
                  <span class="is-size-7 has-text-grey">Remaining</span>
                </th>
                <th class="num-cell">
                  <span>Quantity</span><br/>
                  <span class="is-size-7 has-text-grey">Shipping, Received, Billed</span>
                </th>
                <th>
                  <span>Unit price</span><br/>
                  <span class="is-size-7 has-text-grey">Total</span>
                </th>
                <th>
                  <span>Tarriff %</span><br/>
                  <span class="is-size-7 has-text-grey">Tarriff total</span>
                </th>
                <th>
                  <span>Inbound</span>
                </th>
              </thead>
              <tbody>
                <tr v-for="(pi, i) in po.items" :key="'p-' + po.id + '-i-' + i">
                  <td>{{ pi.line }}</td>
                  <td>
                    <span>{{ pi.itemId }}</span>
                  </td>
                  <td>
                    <span>{{ pi.itemName }}</span>
                  </td>
                  <td>{{ pi.description }}</td>
                  <td class="num-cell">
                    <span>{{ pi.quantityOnHand }}</span><br/>
                    <span class="is-size-7 has-text-grey">{{ pi.quantityAvailable }}</span>
                  </td>
                  <td class="num-cell">
                    <span>{{ pi.quantity }}</span><br/>
                    <span class="is-size-7 has-text-grey">
                      {{ pi.quantityOnShipments + ', ' + pi.quantityReceived + ', ' + pi.quantityBilled }}
                    </span>
                  </td>
                  <td class="num-cell">
                    <span>{{ formatPrice(pi.rate) }}</span><br/>
                    <span class="is-size-7 has-text-grey">{{ formatPrice(pi.amount) }}</span>
                  </td>
                  <td class="num-cell">
                    <span>{{ pi.tarriff == null ? 'N/A' : ('' + pi.tarriff + '%') }}</span><br/>
                    <span class="is-size-7 has-text-grey">{{ pi.tarriff == null ? 'N/A' : (formatPrice(pi.rate * pi.quantity * pi.tarriff / 100)) }}</span>
                  </td>
                  <td>
                    <div v-for="(ibi, j) in pi.inbounds" :key="'ibi-'-i+'-'+j">
                      <router-link v-if="ibi.inbound" :to="vendorId ? ('/vendor-inbound/' + vendorId + '/' + ibi.inbound.id) : ('/inbound/' + ibi.inbound.id)">
                        {{ibi.inbound.shipmentNumber + ' ' + ibi.inbound.nsId + ' ' + ibi.inbound.bookingId }}
                      </router-link>&nbsp;
                      <span>{{ ibi.inboundItem.quantityExpected }}</span>&nbsp;
                      <span>{{ ibi.inboundItem.containerInfo }}</span>&nbsp;
                      <span>{{ ibi.inbound.shipmentStatus }}</span>&nbsp;
                      <span>{{ ibi.inbound.expectedDeliveryDate }}</span>&nbsp;
                      <span>{{ ibi.inbound.actualShippingDate }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>Total</th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th class="num-cell">{{ po.totals.quantity || '' }}</th>
                  <th class="num-cell">{{ po.totals.amount ? formatPrice(po.totals.amount) : '' }}</th>
                  <th class="num-cell">{{ po.totals.tarriff ? formatPrice(po.totals.tarriff) : '' }}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>

          <div v-if="po.poBills && po.poBills.length">
            <article class="message is-success">
              <div class="message-header">
                <p>PO bills:</p>
              </div>
              <div class="message-body">
                <p v-for="(b, i) in po.poBills" :key="'vb-'+i">
                  {{ b }}
                </p>
              </div>
            </article>
          </div>

        </div>
      </div>
      <div v-if="error" class="notification is-danger is-light">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>
    </div>
  </div>
</template>

<script>
import dateFormat from 'dateformat'
import { saveAs } from 'file-saver'


export default {
  name: 'PurchaseOrder',
  data () {
    return {
      error: '',
      waiting: false,
      po: null,
      syncing: false,
      confirming: false,
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    token () {
      return this.$store.state.user.token
    },
    isVendor () {
      return this.$store.state.user.groups.includes('Vendors')
    },
    isVendorAdmin () {
      return this.$store.state.user.groups.includes('VendorAdmin')
    },
    vendorId () {
      return this.$route.params.vendorId
    },
    poId () {
      return this.$route.params.poId
    },
  },
  methods: {
    getPurchaseOrderDetail () {
      this.waiting = true
      this.$http.get(this.server + '/myapp/get-purchase-order-detail/' + this.poId + '/').then(resp => {
        var po = resp.body
        for (var item of po.items) {
          if (po.locationId == 9) {
            item.tarriff = item.dutyPercentageCanada
          } else {
            item.tarriff = item.dutyPercentage
          }
        }
        po.totals = this.computeTotals(po.items)
        this.po = po
        this.waiting = false
      }, err => {
        this.error = err.body
        this.waiting = false
      })
    },
    computeTotals (items) {
      var totals = {
        quantity: 0,
        amount: 0,
        tarriff: 0,
      }
      for (var item of items) {
        totals.quantity += item.quantity
        totals.amount += item.amount
        totals.tarriff += item.tarriff ? 0 : (item.amount * item.tarriff / 100)
      }
      return totals
    },
    syncFromNS () {
      this.syncing = true
      var data = {
        nsId: this.po.nsId,
        enforced: true,
      }
      this.$http.post(this.server + '/myapp/sync-purchase-order/', data).then(resp => {
        this.getPurchaseOrderDetail()
        this.syncing = false
      }, err => {
        this.error = err.body
        this.syncing = false
      })
    },
    confirmPO () {
      var confirm = {
        title: 'Confirm PO',
        message: 'To confirm that you as the wendor have have been notified by this PO and will fullfil it on time.',
        button: 'Yes, I confirm.',
        callback: {
          context: this,
          method: this.confirmPOConfirmed,
          args: []
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    confirmPOConfirmed () {
      this.confirming = true
      var data = {
        poId: this.po.id,
      }
      this.$http.post(this.server + '/myapp/vendor-confirm-purchase-order/', data).then(resp => {
        this.getPurchaseOrderDetail()
        this.confirming = false
      }, err => {
        this.error = err.body
        this.confirming = false
      })
    },
    formatPrice (value) {
      if (!value) {
        return ''
      }
      return '$' + value.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false
      })
    },
    exportItems () {
      var tranId = this.po.tranId
      var rows = this.po.items.map(item => {
        var row = [tranId, item.line.toString(), item.itemId, item.itemName, item.description, item.rate.toString(), item.quantity.toString()]
        return row.join('\t')
      })
      var header = 'PO\tLine\tNS Id\tName\tDescription\tUnit Price\tPO Quantity'
      rows.unshift(header)
      var string = rows.join('\r\n')
      console.log(string)
      var blob = new Blob([string], {type: 'text/plain;charset=utf-8'})
      saveAs(blob, tranId + '.tsv')
    },
  },
  mounted () {
    this.$nextTick(function(){
      this.getPurchaseOrderDetail()
    })
  },
}
</script>

<style scoped>
.sub-items-table {
  font-size: 0.9em;
}
.num-cell {
  text-align: right!important;
}
</style>
